<template>
  <Modal
    :model-value="open"
    show-close
    content-class="max-h-[90vh] overflow-y-auto"
    @update:model-value="emit('update:open', $event)"
  >
    <template #title>{{ areaCharge ? t('editTitle') : t('createTitle') }}</template>

    <form class="flex flex-col gap-4" @submit.prevent="handleSave">
      <Input
        :model-value="name"
        :label="t('name')"
        :is-error="Boolean(nameError)"
        :hint="nameError"
        @update:model-value="name = String($event ?? '')"
      />
      <Checkbox
        v-model="useCustomPrice"
        :label="t('useCustomPrice')"
        @change="handleUseCustomPrice"
      />
      <Select
        v-if="!useCustomPrice"
        :model-value="priceId"
        :label="t('price')"
        :options="priceOptions"
        :is-error="Boolean(priceError)"
        :hint="priceError"
        @update:model-value="priceId = $event ?? ''"
      />
      <Input
        v-else
        :model-value="customPrice"
        :label="t('customPrice')"
        type="number"
        :min="0"
        :max-fraction-digits="2"
        adornment="€"
        :is-error="Boolean(priceError)"
        :hint="priceError"
        @update:model-value="customPrice = $event ?? null"
      />
      <div class="flex flex-col gap-2">
        <p class="text-sm text-obsidian-500">{{ t('mapHint') }}</p>
        <p v-if="geometryError" class="text-sm text-bittersweet-600">{{ geometryError }}</p>
        <AreaChargeMap
          v-if="open"
          :key="areaCharge?.id ?? 'new'"
          height-class="h-[40rem]"
          :initial-geometry="areaCharge?.geometry"
          @change="geometry = $event"
        />
      </div>
      <div class="flex justify-end">
        <CVButton type="submit" :is-loading="isSaving">
          {{ t('save') }}
        </CVButton>
      </div>
    </form>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type {
  AreaCharge,
  AreaChargePriceDto,
  CreateAreaChargeDto,
  UpdateAreaChargeDto,
} from '@carvia/ros-client-types';
import Modal from '@/components/Modal.vue';
import Input from '@/components/Input.vue';
import Select from '@/components/Select.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Checkbox from '@/components/Checkbox.vue';
import AreaChargeMap from '@/views/yield-management/area-charges/AreaChargeMap.vue';
import type { AreaChargeMultiPolygon } from '@/views/yield-management/area-charges/area-charge-geometry.ts';
import { useCreateAreaCharge, useUpdateAreaCharge } from '@/queries/use-area-charges.ts';
import { formatCurrency } from '@/utils/format-numbers.ts';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  open: boolean;
  areaCharge?: AreaCharge | null;
  prices: AreaChargePriceDto[];
}>();

const emit = defineEmits<{
  'update:open': [open: boolean];
}>();

const { t } = useI18n();
const { mutateAsync: createAreaCharge, isPending: isCreating } = useCreateAreaCharge();
const { mutateAsync: updateAreaCharge, isPending: isUpdating } = useUpdateAreaCharge();
const isSaving = computed(() => isCreating.value || isUpdating.value);

const name = ref('');
const useCustomPrice = ref(false);
const priceId = ref('');
const customPrice = ref<number | null>(null);
const geometry = ref<AreaChargeMultiPolygon | null>(null);
const nameError = ref('');
const priceError = ref('');
const geometryError = ref('');

const priceOptions = computed(() =>
  props.prices.map((price) => ({
    label: `${price.name} · ${formatCurrency(price.amount)}`,
    value: price.id,
  })),
);

watch(
  () => [props.open, props.areaCharge] as const,
  ([open, areaCharge]) => {
    if (!open) return;
    name.value = areaCharge?.name ?? '';
    useCustomPrice.value = areaCharge?.customPrice != null;
    priceId.value = areaCharge?.priceId ?? '';
    customPrice.value = areaCharge?.customPrice ?? null;
    geometry.value = null;
    nameError.value = '';
    priceError.value = '';
    geometryError.value = '';
  },
);

const handleUseCustomPrice = (value: boolean) => {
  priceError.value = '';
  if (value) priceId.value = '';
  else customPrice.value = null;
};

const handleSave = async () => {
  const hasPrice = !useCustomPrice.value && Boolean(priceId.value);
  const hasCustom = useCustomPrice.value && customPrice.value != null;
  nameError.value = name.value.trim() ? '' : t('nameRequired');
  priceError.value = hasPrice !== hasCustom ? '' : t('priceRequired');
  geometryError.value = geometry.value ? '' : t('geometryRequired');
  if (nameError.value || priceError.value || geometryError.value || !geometry.value) return;

  const price = hasPrice
    ? { priceId: priceId.value, customPrice: null }
    : { priceId: null, customPrice: customPrice.value };
  if (props.areaCharge) {
    const dto: UpdateAreaChargeDto = {
      name: name.value.trim(),
      geometry: geometry.value,
      ...price,
    };
    await updateAreaCharge({ id: props.areaCharge.id, dto });
  } else {
    const dto: CreateAreaChargeDto = {
      name: name.value.trim(),
      geometry: geometry.value,
      ...(hasPrice ? { priceId: priceId.value } : { customPrice: customPrice.value! }),
    };
    await createAreaCharge(dto);
  }
  emit('update:open', false);
};
</script>

<i18n lang="json">
{
  "en": {
    "createTitle": "New area",
    "editTitle": "Edit area",
    "name": "Name",
    "price": "Price",
    "useCustomPrice": "Use a custom price",
    "customPrice": "Custom price",
    "mapHint": "Draw polygons only. The cut tool punches holes.",
    "save": "Save",
    "nameRequired": "Enter a name.",
    "priceRequired": "Choose a shared price or enter a custom price.",
    "geometryRequired": "Draw at least one polygon."
  },
  "de": {
    "createTitle": "Neues Gebiet",
    "editTitle": "Gebiet bearbeiten",
    "name": "Name",
    "price": "Preis",
    "useCustomPrice": "Eigenen Preis verwenden",
    "customPrice": "Eigener Preis",
    "mapHint": "Zeichne ein Polygon ein. Das Ausschneide-Werkzeug erzeugt Löcher.",
    "save": "Speichern",
    "nameRequired": "Bitte einen Namen eingeben.",
    "priceRequired": "Bitte einen Preis angeben.",
    "geometryRequired": "Bitte mindestens ein Polygon zeichnen."
  },
  "es": {
    "createTitle": "Nueva zona",
    "editTitle": "Editar zona",
    "name": "Nombre",
    "price": "Precio",
    "useCustomPrice": "Usar un precio propio",
    "customPrice": "Precio propio",
    "mapHint": "Solo se permiten polígonos. La herramienta de corte crea huecos.",
    "save": "Guardar",
    "nameRequired": "Introduce un nombre.",
    "priceRequired": "Elige un precio compartido o introduce uno propio.",
    "geometryRequired": "Dibuja al menos un polígono."
  }
}
</i18n>
